import {Map} from "immutable";
import * as React from "react";
import {useState, useCallback} from "react";
import {connect} from "react-redux";
import {Action, bindActionCreators, Dispatch} from "redux";
import {
    allManagersSelector,
    portfolioProductSummariesSelector,
} from "../../../mainReducerMapSelectors";
import {FlashReport} from "../../model/FlashReport.model";
import {FlashReportSummary} from "../../model/FlashReportSummary.model";
import {ManagerResponse} from "../../model/manager/ManagerResponse.model";
import {ProductSummary} from "../../model/product/ProductSummary";
import {prominentButtonStyle} from "../common/buttonStyles";
import {RaisedButton} from "../common/RaisedButton";
import {Brackets, BracketsBox} from "../base/Brackets.component";
import {getSuccessData} from "../common/commonStates";
import {allManagersRequestPair, portfolioProductsRequestPair} from "../common/RequesterPairs";
import {ManagerNameList} from "../manager/ManagerNameList.component";
import {ProductNameList} from "../product/ProductNameList.component";
import {flashReportActions} from "./flashReportActions";
import {multipleApiRequesterWrapper} from "../common/MultipleApiRequesterWrapper";
import {FormattedMessage} from "react-intl";
import {IApplicationRootState} from "../../../applicationState";
import {UserBehaviorTrackerApi} from "../../api/UserBehaviorTrackerApi";
import {getBrowserLocationPath} from "../../api/BrowserWrapper";

interface IFlashReportPropsFromParent {
    id: string;
    flashReport: FlashReport;
    flashReportSummary: FlashReportSummary;
}

interface IFlashReportPropsFromActions {
    actions: {
        getFlashReport: typeof flashReportActions.getFlashReport,
        getFlashReportPDF: typeof flashReportActions.getFlashReportPDF,
    };
}

export interface IFlashReportPropsFromState {
    allManagers: Map<number, ManagerResponse>;
    portfolioProductSummaries: Map<number, ProductSummary>;
}

export type FlashReportComponentProps = IFlashReportPropsFromActions
    & IFlashReportPropsFromParent
    & IFlashReportPropsFromState;

function createMarkup(somehtml: string) {
    return {__html: somehtml};
}

export const FlashReportComponent: React.FC<FlashReportComponentProps> = (props) => {
    const [collapseManagersAndProducts, setCollapseManagersAndProducts] = useState(true);

    const {flashReport, flashReportSummary, allManagers, portfolioProductSummaries, actions} = props;

    const hasLongList = useCallback(() => {
        return flashReportSummary.managerIds.length + flashReportSummary.productIds.length > 3;
    }, [flashReportSummary.managerIds, flashReportSummary.productIds]);

    const getManagerAndProductIds = useCallback(() => {
        const allManagerIds = flashReportSummary.managerIds;
        const allProductIds = flashReportSummary.productIds;

        const [numManagerIds, numProductIds] =
            (hasLongList() && collapseManagersAndProducts)
                ? ((allManagerIds.length > 3) ? [3, 0] : [allManagerIds.length, 3 - allManagerIds.length])
                : [allManagerIds.length, allProductIds.length];

        return {managerIds: allManagerIds.slice(0, numManagerIds), productIds: allProductIds.slice(0, numProductIds)};
    }, [flashReportSummary, collapseManagersAndProducts, hasLongList]);

    const handleDownloadClick = () => {
        if (actions) {
            actions.getFlashReportPDF(flashReportSummary.backstopId, flashReportSummary.fileName);
            UserBehaviorTrackerApi.trackUserBehavior(getBrowserLocationPath() + "/download");
        }
    };

    const handleViewAllClick = () => {
        setCollapseManagersAndProducts(!collapseManagersAndProducts);
    };

    const {managerIds, productIds} = getManagerAndProductIds();
    const viewText = collapseManagersAndProducts ? "View All" : "View Less";

    return (
        <div className="note-container no-bullets new-common-styles">
            <div className="strategy-update__header">
                <div className={"header-wrapper"}>
                    <h1 id="flashreport-title">
                        {flashReportSummary.title}
                    </h1>
                </div>
                <RaisedButton
                    className="pdf-download-button"
                    style={prominentButtonStyle}
                    primary={true}
                    onClick={handleDownloadClick}>
                    <span className="pdf-download-button-body">
                        <span className="pdf-download-button-text">
                            <FormattedMessage
                                id="updates.download-pdf"
                                defaultMessage="Download PDF"
                            />
                        </span>
                        <span className="fal fa-download" />
                    </span>
                </RaisedButton>
            </div>
            <div className="attachments">
                <ManagerNameList managerIds={managerIds} allManagers={allManagers} />
                <ProductNameList productIds={productIds} portfolioProductSummaries={portfolioProductSummaries} />
                {hasLongList() && (
                    // TODO: Remove
                    <div className={`view-all link-14 clickable ${collapseManagersAndProducts ? "list-collapsed" : "list-expanded"}`}
                         onClick={handleViewAllClick}>{viewText}</div>
                )}
            </div>
            <Brackets>
                <BracketsBox>
                    <div className="body-16">
                        <div id="flashreport-content"
                             className="update-content"
                             dangerouslySetInnerHTML={createMarkup(flashReport.descriptionHtml || "NA")}
                             data-testid="flashreport-content" />
                    </div>
                </BracketsBox>
            </Brackets>
        </div>
    );
};

export const mapStateToProps = (state: IApplicationRootState) => {
    return {
        allManagers: getSuccessData(allManagersSelector(state))!,
        portfolioProductSummaries: getSuccessData(portfolioProductSummariesSelector(state))!,
    };
};

export const mapDispatchToProps = (dispatch: Dispatch<Action>): IFlashReportPropsFromActions => {
    return {
        actions: bindActionCreators(flashReportActions, dispatch),
    };
};

const connectedComponent = connect<IFlashReportPropsFromState,
    IFlashReportPropsFromActions, {}, IApplicationRootState>(
    mapStateToProps,
    mapDispatchToProps,
)(FlashReportComponent);

export default multipleApiRequesterWrapper(
    connectedComponent,
    [
        allManagersRequestPair,
        portfolioProductsRequestPair(),
    ],
);
